import { tmxApiCall } from './index'
import { RegexConstants } from '../constants.js'

// Utilities
import Gps from '../utility/gpsUtil'
import Logger from '../utility/loggerUtil'

export default {
  async verifyPaymentLocation (longitude, latitude, acctState, loanId, lmsId, storeId, transactionType, recordOnly = false) {
    Logger.info('Starting Verify Payment Location query.')
    return tmxApiCall('get', '/refiallowedstatus', {
      params: {
        latitude,
        longitude,
        acctState,
        loanId,
        lmsId,
        recordOnly,
        storeId,
        transactionType
      }
    })
  },

  async recordPaymentLocation (longitude, latitude, acctState, loanId, lmsId, storeId, transactionType, recordOnly = true) {
    Logger.info('Starting Record Payment Location query.')
    return tmxApiCall('get', '/refiallowedstatus', {
      params: {
        latitude,
        longitude,
        acctState,
        loanId,
        lmsId,
        recordOnly,
        storeId,
        transactionType
      }
    })
  },

  async saveRestrictedStateAcknowledgement (loanId, timeStamp, didConsent, lmsId, loanState, storeId, transactionType) {
    Logger.info('Starting Save Restricted State Acknowledgement query.')
    return tmxApiCall('post', '/refiallowedstatus', {
      loanId,
      timeStamp,
      consent: didConsent,
      lmsId,
      loanState,
      storeId,
      transactionType
    })
  },

  async getPayFrequencies () {
    Logger.info('Starting Get Payment Frequencies query.')
    return tmxApiCall('get', '/payFrequency')
  },

  /**
   * Posts to /payment endpoint with payment details.
   * @param {Object} paymentInfo - The object containing the paymentInfo for the loan.
   * @returns {Promise<{messageKey: String}>} The key to check the payment response status.
   */
  async postPayment (paymentInfo) {
    Logger.info('Started post loan payment.')

    return tmxApiCall('post', '/payment', {
      confirmationDetails: {
        email: paymentInfo.email,
        phoneNumber: paymentInfo.phoneNumber,
        cardHolderName: paymentInfo.cardHolderName,
        loanId: paymentInfo.loanId,
        amountPaid: parseFloat(paymentInfo.paymentAmount).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
        cardNumber: paymentInfo.cardNumber.substring(paymentInfo.cardNumber.length - 4),
        customerName: paymentInfo.customerName,
        storePhoneNumber: paymentInfo.storePhoneNumber,
        productBrand: paymentInfo.productBrand,
        collateralYear: paymentInfo.collateralYear,
        collateralMake: paymentInfo.collateralMake,
        collateralModel: paymentInfo.collateralModel,
        isPayoff: paymentInfo.isPayoff === true,
        isPartial: paymentInfo.isPartial === true,
        paymentDueDate: paymentInfo.paymentDueDate,
        username: paymentInfo.username
      },
      storeId: paymentInfo.storeId,
      loanId: paymentInfo.loanId,
      paymentAmount: paymentInfo.paymentAmount,
      cardIds: paymentInfo.paymentCardIds,
      disburseCardId: paymentInfo.disburseCardId,
      isRefi: paymentInfo.isRefi,
      autoPay: paymentInfo.autoPay,
      disbursementAmount: paymentInfo.disbursementAmount,
      disbursementType: paymentInfo.disbursementType,
      lmsId: paymentInfo.lmsId,
      intentId: paymentInfo.intentId,
      migrateProduct: paymentInfo.migrateProduct,
      ccv: paymentInfo.cvv,
      timeZone: Gps.getTimezone(),
      paymentType: paymentInfo.paymentType,
      guestPayment: paymentInfo.guestPayment,
      riskCheck: paymentInfo.riskCheck,
      ofrPaymentValue: paymentInfo.ofrPaymentValue
    })
  },

  /**
   * Gets the payment response from /payment.
   * @param {String} messageKey - The key associated with the payment.
   * @returns {Promise<{Error: String, confirmationNumber: String, newLoanId: String, newLoanDueDate: String, migrateProductUrl: String, newPrincipalBalance: Number}>} Payment result information.
   */
  async getPaymentStatus (messageKey) {
    Logger.info('Started Check Pay Loan status query.')
    return tmxApiCall('get', '/payment', {
      params: {
        messageKey
      }
    })
  },

  /**
 * Checks for disbursement reference details (currently only used for push to debit and MoneyGram).
 * @param {String} loanId - The identifier for a Vergent loan.
 * @param {String} disbursementType - Indicates the type of disbursement.
 * @returns {Promise<{success: Boolean, referenceNumber: String, email: String}>} Return data object.
 */
  async getReferenceNumber (loanId, disbursementType) {
    Logger.info('Checking for disbursement reference number')
    return tmxApiCall('get', '/disbursementReference', {
      params: {
        loanId,
        disbursementType
      }
    })
  },

  /**
   * Sets the payment timeout flag so customers will be notified when refi docs are ready
   * @param {String} loanId - The identifier for a Vergent loan.
   * @param {Number} paymentAmount - The amount of the payment.
   * @returns {Promise<{success: Boolean}>} Returns boolean for success or failure.
   */
  async setPaymentTimeout (loanId, paymentAmount) {
    Logger.info('Setting Payment Timeout')
    return tmxApiCall('post', '/paymentTimeout', {
      loanId,
      paymentAmount
    })
  },

  /**
   * Gets demographic reference information
   * @param {Number} productId - The config identifier for a product
   * @param {String} customerId - The identifier for a customer
   * @param {String} loanId - The identifier for a loan
   * @returns {Promise<{demographicReference: {genderOptions: [], heightOptions: {minFeet: Number, maxFeet: Number}, demographicOptions: {gender: Boolean, height: Boolean, race: Boolean, weight: Boolean}, raceOptions: []}}>}
   */
  async getDemographicReferences (productId, customerId, loanId) {
    Logger.info('Started Get Demographic References')
    return tmxApiCall('get', '/demographicReference', {
      params: {
        productId,
        customerId,
        loanId
      }
    })
  },

  /**
   * Checks if the user is locked from doing add on loans; it will not lock the user.
   * @returns {Promise<{isLocked: Boolean}>} Object with isLocked property.
   */
  async isAddOnLocked () {
    Logger.info('Started check for add on lock.')
    return tmxApiCall('get', '/addOnLock')
  },

  /**
   * Checks if the user is locked from doing add on loans; has the possibility of locking the user.
   * @param {String} loanId - The identifier for a loan.
   * @param {String} storeId - The identifier for a store.
   * @param {String} [latitude] - The X coordinate of the gps position of the customer.
   * @param {String} [longitude] - The Y coordinate of the gps position of the customer.
   * @returns {Promise<{isLocked: Boolean}>} Object with isLocked property.
   */
  async tryAddOnLock (loanId, storeId, latitude, longitude) {
    Logger.info('Started try/check for add on lock.')
    return tmxApiCall('post', '/addOnLock', {
      loanId,
      storeId,
      latitude,
      longitude
    })
  }
}
